import type { User } from "@/shared/types/auth.types";
import { getRoleDefinition } from "./roleDefinitions";

// Role inheritance resolution service
export class RoleResolver {
  private static instance: RoleResolver;

  static getInstance(): RoleResolver {
    if (!RoleResolver.instance) {
      RoleResolver.instance = new RoleResolver();
    }
    return RoleResolver.instance;
  }

  // Get permissions for a role including inheritance
  getRolePermissions(roleName: string): string[] {
    const allPermissions = new Set<string>();
    const visited = new Set<string>();

    this.collectPermissions(roleName, allPermissions, visited);
    return Array.from(allPermissions);
  }

  // Get user's effective roles (including inherited)
  getUserRoles(user: User): string[] {
    return Array.isArray(user.roles) ? [...new Set(user.roles)] : [];
  }

  // Backend session.permissions is the only source of truth for RBAC permissions.
  getUserPermissions(user: User): string[] {
    return Array.isArray(user.permissions) ? [...new Set(user.permissions)] : [];
  }

  // Check if user has specific role (including inherited)
  hasRole(user: User, role: string): boolean {
    return this.getUserRoles(user).includes(role);
  }

  // Check if user has any of the specified roles
  hasAnyRole(user: User, roles: string[]): boolean {
    const userRoles = this.getUserRoles(user);
    return roles.some((role) => userRoles.includes(role));
  }

  private collectPermissions(
    roleName: string,
    permissions: Set<string>,
    visited: Set<string>,
  ): void {
    if (visited.has(roleName)) return; // Prevent circular inheritance
    visited.add(roleName);

    const roleDefinition = getRoleDefinition(roleName);
    if (!roleDefinition) return;

    // Add direct permissions
    roleDefinition.permissions.forEach((perm) => permissions.add(perm));

    // Add inherited permissions
    roleDefinition.inherits?.forEach((inheritedRole) => {
      this.collectPermissions(inheritedRole, permissions, visited);
    });
  }

  private collectInheritedRoles(roleName: string, roles: Set<string>): void {
    const roleDefinition = getRoleDefinition(roleName);
    if (!roleDefinition?.inherits) return;

    roleDefinition.inherits.forEach((inheritedRole) => {
      roles.add(inheritedRole);
      this.collectInheritedRoles(inheritedRole, roles);
    });
  }
}
